import React from 'react';
import { Loader2 } from 'lucide-react';

export interface LoadingStateProps {
  /** Loading display variant */
  variant?: 'spinner' | 'skeleton' | 'dots';
  /** Number of skeleton rows (for skeleton variant) */
  rows?: number;
  /** Optional text shown below the spinner */
  text?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Canonical loading state – replaces 6+ custom spinner/skeleton patterns.
 *
 * Variants:
 * - `spinner`: Centered Loader2 icon with optional text
 * - `skeleton`: Shimmer rows (uses the skel-pulse animation from tokens)
 * - `dots`: Three animated dots
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  variant = 'spinner',
  rows = 4,
  text,
  className = '',
}) => {
  if (variant === 'skeleton') {
    return (
      <div className={`flex flex-col gap-2.5 animate-fade-in ${className}`}>
        {/* Avatar + text row */}
        <div className="flex items-center gap-2.5">
          <div className="skeleton w-10 h-10 rounded-full flex-shrink-0" />
          <div className="flex-1 flex flex-col gap-2">
            <div className="skeleton h-3 w-[55%] rounded-xs" />
            <div className="skeleton h-2.5 w-[35%] rounded-xs" />
          </div>
        </div>
        {/* Content rows */}
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="skeleton h-3 rounded-xs"
            style={{ width: `${100 - i * 12}%` }}
          />
        ))}
        {/* Large block */}
        <div className="skeleton h-20 w-full rounded-md mt-1" />
      </div>
    );
  }

  if (variant === 'dots') {
    return (
      <div className={`flex items-center justify-center gap-1 py-12 ${className}`}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-2 h-2 rounded-full bg-accent"
            style={{
              animation: `dotPulse 1.2s ease-in-out ${i * 0.2}s infinite`,
            }}
          />
        ))}
        <style>{`
          @keyframes dotPulse {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `}</style>
      </div>
    );
  }

  // Default: spinner
  return (
    <div className={`flex flex-col items-center justify-center py-16 gap-3 ${className}`}>
      <Loader2 className="w-8 h-8 text-accent animate-spin" />
      {text && (
        <span className="text-sm text-txt-secondary">{text}</span>
      )}
    </div>
  );
};

export default LoadingState;
